/**
 * Resolves the map key used for a stock code in preparedInfo or legacy item maps.
 */
export function resolveAutoOrderPreparedItemKey(
  record: Record<string, unknown>,
  code: string,
): string | undefined {
  if (code in record) {
    return code;
  }

  const encodedCode: string = encodeURIComponent(code);
  if (encodedCode in record) {
    return encodedCode;
  }

  for (const key of Object.keys(record)) {
    try {
      if (decodeURIComponent(key) === code) {
        return key;
      }
    } catch {
      continue;
    }
  }

  return undefined;
}

/**
 * Legacy orderItem documents store stock code under field "0".
 */
const AUTO_ORDER_ITEM_STOCK_CODE_FIELD = '0';

/**
 * Returns true when a stock code matches a Firestore orderItem document id.
 */
export function autoOrderItemDocMatchesCode(docId: string, code: string): boolean {
  if (docId === code) {
    return true;
  }

  try {
    return decodeURIComponent(docId) === code;
  } catch {
    return false;
  }
}

/**
 * Returns true when document data contains the stock code in a known field.
 */
function orderItemDataContainsStockCode(docData: Record<string, unknown>, code: string): boolean {
  const dataCode: unknown = docData['code'];
  if (typeof dataCode === 'string' && autoOrderItemDocMatchesCode(dataCode, code)) {
    return true;
  }
  if (typeof dataCode === 'number' && String(dataCode) === code) {
    return true;
  }

  const legacyCode: unknown = docData[AUTO_ORDER_ITEM_STOCK_CODE_FIELD];
  if (typeof legacyCode === 'string' && legacyCode === code) {
    return true;
  }
  if (typeof legacyCode === 'number' && String(legacyCode) === code) {
    return true;
  }

  return false;
}

/**
 * Returns true when an orderItems subcollection document should be removed for a stock code.
 */
export function orderItemSubcollectionDocMatchesCode(
  docId: string,
  docData: Record<string, unknown>,
  code: string,
): boolean {
  if (autoOrderItemDocMatchesCode(docId, code)) {
    return true;
  }

  return orderItemDataContainsStockCode(docData, code);
}

/**
 * Path segments used as orderItem document ids for a stock code.
 */
export function getAutoOrderItemDocIdCandidates(code: string): string[] {
  return [...new Set([code, encodeURIComponent(code)])];
}
